import { CalendarMonth as CalendarIcon } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Menu,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import React, { useState, useCallback } from "react";

import { useCelleraTheme } from "../../theme/useCelleraTheme";

export interface MonthPickerCalendarProps {
  /**
   * Currently selected month (1-12)
   */
  selectedMonth: number;
  /**
   * Currently selected year
   */
  selectedYear: number;
  /**
   * Callback when month/year selection changes
   */
  onDateChange: (month: number, year: number) => void;
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  /**
   * Custom styling for the component
   */
  sx?: SxProps<Theme>;
}

const MonthPickerCalendar: React.FC<MonthPickerCalendarProps> = ({
  selectedMonth,
  selectedYear,
  onDateChange,
  disabled = false,
  sx,
}) => {
  const { colors } = useCelleraTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMonthYearSelect = useCallback(
    (month: number, year: number) => {
      onDateChange(month, year);
      handleClose();
    },
    [onDateChange],
  );

  return (
    <Box sx={sx}>
      <IconButton
        onClick={handleClick}
        disabled={disabled}
        sx={{
          color: colors.text.secondary,
          "&:hover": {
            color: colors.primary.main,
            backgroundColor: colors.primary.light + "20",
          },
        }}
        size="small"
        title="Select month and year"
      >
        <CalendarIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 400,
              width: 280,
              mt: 1,
              border: `1px solid ${colors.border.light}`,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* Year Selection */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border.light}` }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: colors.text.primary }}
          >
            Select Year
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {years.map((year) => (
              <Box
                key={year}
                onClick={() => handleMonthYearSelect(selectedMonth, year)}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  backgroundColor:
                    selectedYear === year ? colors.primary.main : "transparent",
                  color:
                    selectedYear === year ? "white" : colors.text.secondary,
                  "&:hover": {
                    backgroundColor:
                      selectedYear === year
                        ? colors.primary.dark
                        : colors.primary.light + "20",
                  },
                  minWidth: 60,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: selectedYear === year ? 600 : 400 }}
                >
                  {year}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Month Selection */}
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: colors.text.primary }}
          >
            Select Month
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {months.map((month, index) => (
              <Box
                key={month}
                onClick={() => handleMonthYearSelect(index, selectedYear)}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  backgroundColor:
                    selectedMonth === index
                      ? colors.primary.main
                      : "transparent",
                  color:
                    selectedMonth === index
                      ? colors.text.inverse
                      : colors.text.secondary,
                  "&:hover": {
                    backgroundColor:
                      selectedMonth === index
                        ? colors.primary.dark
                        : colors.primary.light + "20",
                  },
                  minWidth: 80,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: selectedMonth === index + 1 ? 600 : 400 }}
                >
                  {month}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default MonthPickerCalendar;
